import React, { useEffect, useState } from 'react';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { getAolGroupForDateTime, getContentsInTankAtDate, getTankTypeDisplayName, isBunkeringOnboardAtDate } from '../BunkeringUtils';
import BunkerRegistrationTank from './BunkerRegistrationTank';
import { BunkerReceived } from 'src/types/ViewModel/Bunkering/BunkerReceived';
import { FuelReceivedTableBodyStyle, FuelReceivedTableHeadStyle, FuelReceivedTableRowStyle, FuelReceivedTableStyle } from '../Bunkering.styles';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { useAolStore } from 'src/context/AolContext';
import { useBunkerContext } from 'src/context/BunkerContext';
import { isDateSameOrAfter, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { LOCKED_FOR_CHANGE_STATUSES } from './status/BunkerRegStatusUtils';

interface Props {
    fuelTanks: Tank[];
    selectedBunkering: Bunkering;
    bunkerings: Bunkering[];
    updateBunkerReceivedPerTanks: (bunkerReceivedPerTanks: BunkerReceived[]) => void;
}

const FuelReceivedTankTable: React.FC<Props> = ({ fuelTanks, selectedBunkering, bunkerings, updateBunkerReceivedPerTanks }) => {
    const { aolGroups, aolDataItems, aolFilter } = useAolStore();
    const { fuelRobData } = useBunkerContext();
    const [bunkerReceivedPerTanks, setBunkerReceivedPerTanks] = useState<BunkerReceived[]>([]);
    const [currentContents, setCurrentContents] = useState<{ [key: number]: string | null }>({});

    useEffect(() => {
        setBunkerReceivedPerTanks(selectedBunkering.bunkerReceivedPerTanks);
    }, [selectedBunkering]);

    useEffect(() => {
        const updatedContents: { [key: number]: string | null } = {};
        fuelTanks.forEach((tank) => {
            updatedContents[tank.tankId] = getCurrentContent(tank.tankId);
        });
        setCurrentContents(updatedContents);
    }, [selectedBunkering, fuelTanks, bunkerings, aolGroups, aolDataItems, fuelRobData]);

    const getCurrentContent = (tankId: number): string | null => {
        if (
            aolFilter &&
            isDateSameOrAfter(selectedBunkering.bunkeringDateTime, aolFilter.fromDate) &&
            isDateSameOrBefore(selectedBunkering.bunkeringDateTime, aolFilter.toDate)
        ) {
            // if the selected bunkering is within the period for which we have selected in AoL filter and have AoL / ROB data in memory, check contents based on ROB
            const currentBunkerings = getContentsInTankAtDate(
                bunkerings,
                tankId,
                selectedBunkering.bunkeringDateTime,
                getAolGroupForDateTime(selectedBunkering.bunkeringDateTime, aolGroups),
                aolDataItems,
                fuelRobData
            )
                .filter((b) => b.bunkeringGuid !== selectedBunkering.bunkeringGuid)
                ?.map((b) => b.bunkeringReference);
            return currentBunkerings?.join(', ') ?? null;
        } else {
            // if the selected bunkering is outside the period for which we have selected in AoL filter, check contents based on status and original filled tanks
            const currentBunkerings = bunkerings
                .filter(
                    (b) =>
                        b.bunkeringGuid !== selectedBunkering.bunkeringGuid &&
                        isBunkeringOnboardAtDate(b, selectedBunkering.bunkeringDateTime) &&
                        b.bunkerReceivedPerTanks.some((br) => br.tankId === tankId)
                )
                ?.map((b) => b.bunkeringReference);
            return currentBunkerings?.join(', ') ?? null;
        }
    };

    const addTank = (tank: Tank, fuelReceived: number) => {
        const updatedList = [
            ...bunkerReceivedPerTanks,
            {
                bunkerInTankId: 0,
                tankId: tank.tankId,
                bunkeringId: selectedBunkering.bunkeringId,
                bunkeringGuid: selectedBunkering.bunkeringGuid,
                bunkerReceivedMt: fuelReceived,
            },
        ];
        setBunkerReceivedPerTanks(updatedList);
        updateBunkerReceivedPerTanks(updatedList);
    };

    const removeTank = (tank: Tank) => {
        const updatedList = bunkerReceivedPerTanks.filter((br) => br.tankId !== tank.tankId);
        setBunkerReceivedPerTanks(updatedList);
        updateBunkerReceivedPerTanks(updatedList);
    };

    const changeFuelReceived = (tank: Tank, fuelReceived: number) => {
        const updatedList = bunkerReceivedPerTanks.map((br) => {
            if (br.tankId === tank.tankId) {
                return {
                    ...br,
                    bunkerReceivedMt: fuelReceived,
                };
            }
            return br;
        });
        setBunkerReceivedPerTanks(updatedList);
        updateBunkerReceivedPerTanks(updatedList);
    };

    return (
        <FuelReceivedTableStyle>
            <FuelReceivedTableHeadStyle>
                <FuelReceivedTableRowStyle>
                    <th>Tank Name</th>
                    <th>Tank Type</th>
                    <th>Volume</th>
                    <th>Current Content</th>
                    <th>Fuel Received</th>
                </FuelReceivedTableRowStyle>
            </FuelReceivedTableHeadStyle>
            <FuelReceivedTableBodyStyle>
                {fuelTanks.map((tank) => (
                    <FuelReceivedTableRowStyle key={`${selectedBunkering.bunkeringGuid}_${tank.tankId}`}>
                        <td>{tank.tankName}</td>
                        <td>{getTankTypeDisplayName(tank.tankType)}</td>
                        <td>{tank.volume} m³</td>
                        <td>{currentContents[tank.tankId] ?? 'Empty'}</td>
                        <td>
                            <BunkerRegistrationTank
                                tank={tank}
                                literWeight={
                                    selectedBunkering.bdnLiterWeight && selectedBunkering.bdnLiterWeight > 0 ? selectedBunkering.bdnLiterWeight : null
                                }
                                bunkerReceived={bunkerReceivedPerTanks.find((br) => br.tankId === tank.tankId)}
                                addTank={addTank}
                                removeTank={removeTank}
                                changeFuelReceived={changeFuelReceived}
                                readOnly={LOCKED_FOR_CHANGE_STATUSES.includes(selectedBunkering.currentStatus?.status)}
                                selectedBunkering={selectedBunkering}
                            />
                        </td>
                    </FuelReceivedTableRowStyle>
                ))}
            </FuelReceivedTableBodyStyle>
        </FuelReceivedTableStyle>
    );
};

export default FuelReceivedTankTable;
